/** Normalizes production ChatTransport chunks into the ChatBox renderer vocabulary (`dynamic-tool` / `data-artifact`), and reconciles assembled text parts against the authoritative final answer. */

import type { AiSdkUIMessageChunk } from "@mui/x-chat"
import type { ChatMessage, ChatMessagePart, ChatTextMessagePart } from "@mui/x-chat-headless"
import {
  THREAD_METADATA_CHUNK_TYPE,
  TURN_FAILURE_METADATA_CHUNK_TYPE,
  STREAM_RETRYING_CHUNK_TYPE,
} from "connected-components/ai-teammate/utils/chat-transport"
import { type ArtifactEnvelope, isArtifactEnvelope } from "./artifact-contract"
import { UNTITLED_THREAD_LABEL } from "../constants"

// Re-surface the shared transport's chunk type for this module's consumers (the value is owned by the transport).
export { THREAD_METADATA_CHUNK_TYPE, TURN_FAILURE_METADATA_CHUNK_TYPE, STREAM_RETRYING_CHUNK_TYPE }

/** A calm, terminal error chunk — the MUI adapter surfaces it as an inline message error. */
export function errorChunk(message: string, kind?: string): AiSdkUIMessageChunk {
  return { type: "error", errorText: message, ...(kind != null && { errorKind: kind }) }
}

/** The SERVER's exception class, when the transport named one (SSE `error` events only). Not this app's taxonomy. */
export function errorTypeOf(chunk: AiSdkUIMessageChunk): string | null {
  const type = (chunk as { errorType?: unknown }).errorType
  return typeof type === "string" && type.length > 0 ? type : null
}

/**
 * The rendered copy on an error chunk. The chunk type widens every field to `unknown`, and this is only ever
 * read to carry a failure onward — nothing renders it — so an absent one becomes a neutral placeholder.
 */
export function errorTextOf(chunk: AiSdkUIMessageChunk): string {
  const text = (chunk as { errorText?: unknown }).errorText
  return typeof text === "string" ? text : "The turn ended with an unreported error."
}

/**
 * This APP's taxonomy bucket for an error chunk, where the producer could name one (the buffered POST, which
 * classified the HTTP error to build its message). `null` otherwise. `errorText` is rendered copy —
 * a calm sentence written for the reader — so re-classifying it downstream recovers nothing; the producer is
 * the only place the class is still known. Rides as an extra key, which the renderer ignores.
 */
export function errorKindOf(chunk: AiSdkUIMessageChunk): string | null {
  const kind = (chunk as { errorKind?: unknown }).errorKind
  return typeof kind === "string" && kind.length > 0 ? kind : null
}

function isTextPart(part: ChatMessagePart): part is ChatTextMessagePart {
  return part.type === "text" && typeof (part as { text?: unknown }).text === "string"
}

/** The concatenated text of a ChatBox message's `text` parts. */
export function messageText(message: Pick<ChatMessage, "parts">): string {
  return message.parts
    .filter(isTextPart)
    .map((part) => part.text)
    .join("")
}

function nonEmptyString(value: unknown): string | null {
  return typeof value === "string" && value !== "" ? value : null
}

function attachmentFilenameOf(part: ChatMessagePart): string | null {
  if (part.type === "file") return nonEmptyString(part.filename)
  if (part.type !== "data-attachment") return null
  const data: unknown = part.data
  if (typeof data !== "object" || data === null) return null
  return nonEmptyString((data as { filename?: unknown }).filename)
}

export function threadTitleOf(message: Pick<ChatMessage, "parts">): string {
  const text = messageText(message).trim()
  if (text !== "") return text
  for (const part of message.parts) {
    const filename = attachmentFilenameOf(part)
    if (filename !== null) return filename
  }
  return UNTITLED_THREAD_LABEL
}

/** The last text part with its index — the answer, per ADR 0009 — or `null` when the message has none. */
function findAnswerPart(parts: ChatMessagePart[]): { index: number, part: ChatTextMessagePart } | null {
  for (let index = parts.length - 1; index >= 0; index -= 1) {
    const part = parts[index]
    if (isTextPart(part)) return { index, part }
  }
  return null
}

/** Settle the answer (ADR 0009: the last text part) on the authoritative `final_message.text`, dropping the earlier parts a repeated or overlapping delta duplicated it into (APP-3466). */
export function settleFinalAnswerParts(parts: ChatMessagePart[], finalText: string): ChatMessagePart[] {
  if (finalText.trim() === "") return parts
  const answer = findAnswerPart(parts)
  if (answer === null) return parts
  const { index: lastTextIndex, part: answerPart } = answer
  // Prefix, not substring: a duplicated run restates the answer from its start, so narration matching mid-answer survives.
  let firstDuplicatedTextIndex = lastTextIndex
  while (firstDuplicatedTextIndex > 0) {
    const candidate = parts[firstDuplicatedTextIndex - 1]
    if (!isTextPart(candidate) || !finalText.startsWith(candidate.text)) break
    firstDuplicatedTextIndex -= 1
  }
  if (firstDuplicatedTextIndex === lastTextIndex && answerPart.text === finalText) return parts
  return [
    ...parts.slice(0, firstDuplicatedTextIndex),
    { ...answerPart, text: finalText },
    ...parts.slice(lastTextIndex + 1),
  ]
}

const TOOL_INPUT_CHUNK_TYPES = new Set(["tool-input-start", "tool-input-available", "tool-approval-request"])

/** Turn identifiers both transports put on the `finish` chunk's metadata; the ONE transport-independent place to capture them (MUI drops finish metadata). */
export interface ChatFinishMetadata {
  threadId?: string
  messageId?: string
  text?: string
  artifacts: ArtifactEnvelope[]
}

export type FinishMetadataSink = (metadata: ChatFinishMetadata) => void

/** Tap for the new thread's id, surfaced BEFORE finish via the `thread_created` event's `thread-metadata` chunk. */
export type EarlyThreadIdSink = (threadId: string) => void

/** Tap for the assistant row a FAILED turn persisted, so the next send parents on the real head.
 * `threadId` is the thread that row belongs to, so the consumer can ignore a turn the view has left. */
export type FailedTurnAssistantIdSink = (assistantMessageId: string, threadId: string | null) => void

/** Fires once the transport stream fully drains; `committed` is true when a real finish (a finish with a messageId) was seen. */
export type SettledSink = (committed: boolean) => void

/** The optional taps `normalizeChunkStream` accepts. Named rather than positional: four same-shaped
 * callbacks in a row swap silently, and TypeScript only catches it when their parameter types differ. */
export interface ChunkStreamSinks {
  onFinishMetadata?: FinishMetadataSink
  onEarlyThreadId?: EarlyThreadIdSink
  onSettled?: SettledSink
  onFailedTurnAssistantId?: FailedTurnAssistantIdSink
  /** Reports whether THIS chunk is the provider-stall retry signal, so a consumer can raise the retrying
   * state on the signal and drop it the moment any other chunk flows. */
  onStreamRetryingChange?: (retrying: boolean) => void
}

/**
 * The early-metadata chunk the transport emits for a `thread_created` event (its own bespoke type, not an
 * AI-SDK message part). It rides the unknown-chunk fall-through: the drawer's `ai@7.0.18` runtime and MUI's
 * adapter both pass an unrecognized `type` through untouched — never rendered — so it's a no-op for every
 * consumer except this module's `earlyThreadIdOf` tap.
 */
export interface ThreadMetadataChunk {
  type: typeof THREAD_METADATA_CHUNK_TYPE
  threadId: string
  // Index signature so this bespoke chunk stays assignable to the AI-SDK `{ type: string; [key: string]: unknown }`
  // union member — required for the `chunk is ThreadMetadataChunk` type predicate below to compile.
  [key: string]: unknown
}

/** The bespoke chunk a failed turn emits with the id of the assistant row it persisted. Rides the same
 * unknown-chunk fall-through as `ThreadMetadataChunk`, so it never renders. */
export interface TurnFailureMetadataChunk {
  type: typeof TURN_FAILURE_METADATA_CHUNK_TYPE
  assistantMessageId: string
  threadId?: string
  [key: string]: unknown
}

/** Narrow a transport chunk to the failed-turn metadata chunk (a real `assistantMessageId` required). */
export function isTurnFailureMetadataChunk(chunk: AiSdkUIMessageChunk): chunk is TurnFailureMetadataChunk {
  return chunk.type === TURN_FAILURE_METADATA_CHUNK_TYPE && typeof (chunk as { assistantMessageId?: unknown }).assistantMessageId === "string"
}

/** The assistant row a failed turn persisted with its thread, or `null` for any other chunk. */
export function failedTurnAssistantIdOf(
  chunk: AiSdkUIMessageChunk,
): { assistantMessageId: string, threadId: string | null } | null {
  if (!isTurnFailureMetadataChunk(chunk)) return null
  return {
    assistantMessageId: chunk.assistantMessageId,
    threadId: typeof chunk.threadId === "string" ? chunk.threadId : null,
  }
}

/** Narrow a transport chunk to the early `thread-metadata` chunk (a real `threadId` string required). */
export function isThreadMetadataChunk(chunk: AiSdkUIMessageChunk): chunk is ThreadMetadataChunk {
  return chunk.type === THREAD_METADATA_CHUNK_TYPE && typeof (chunk as { threadId?: unknown }).threadId === "string"
}

/** Narrow a transport chunk to the provider-stall retry signal (the `stream_retrying` event). */
export function isStreamRetryingChunk(chunk: AiSdkUIMessageChunk): boolean {
  return chunk.type === STREAM_RETRYING_CHUNK_TYPE
}

function metadataOf(chunk: AiSdkUIMessageChunk): Record<string, unknown> | null {
  if (!("messageMetadata" in chunk)) return null
  const metadata: unknown = chunk.messageMetadata
  if (typeof metadata !== "object" || metadata === null) return null
  return metadata as Record<string, unknown>
}

/** The narrowed metadata of a `finish` chunk, or `null` for any other chunk. */
export function finishMetadataOf(chunk: AiSdkUIMessageChunk): ChatFinishMetadata | null {
  if (chunk.type !== "finish") return null
  const metadata = metadataOf(chunk)
  if (metadata === null) return null
  const artifacts = Array.isArray(metadata.artifacts) ? metadata.artifacts.filter(isArtifactEnvelope) : []
  return {
    threadId: typeof metadata.threadId === "string" ? metadata.threadId : undefined,
    messageId: typeof metadata.messageId === "string" ? metadata.messageId : undefined,
    text: typeof metadata.text === "string" ? metadata.text : undefined,
    artifacts,
  }
}

/** The new thread's id from the early `thread-metadata` chunk (the `thread_created` event), or `null` for any other chunk. */
export function earlyThreadIdOf(chunk: AiSdkUIMessageChunk): string | null {
  return isThreadMetadataChunk(chunk) ? chunk.threadId : null
}

/** Normalize one transport chunk into the renderer vocabulary (1..n chunks out). */
export function normalizeChunk(chunk: AiSdkUIMessageChunk): AiSdkUIMessageChunk[] {
  if (
    chunk.type === THREAD_METADATA_CHUNK_TYPE
    || chunk.type === TURN_FAILURE_METADATA_CHUNK_TYPE
    || chunk.type === STREAM_RETRYING_CHUNK_TYPE
  ) {
    // Early metadata is tapped by normalizeChunkStream before this; keep it out of the renderer vocabulary.
    return []
  }
  if (chunk.type === "error") {
    // `errorKind` is a transport-internal signal for the turn-outcome seam, which reads the chunk upstream of
    // this (guardedStream wraps the source; the adapter normalizes it). Dropped here so the renderer keeps
    // seeing exactly the calm error and nothing else.
    return [{ type: "error", errorText: errorTextOf(chunk) }]
  }
  if (TOOL_INPUT_CHUNK_TYPES.has(chunk.type)) {
    // Our tools are server-defined: flag `dynamic` so the runtime makes `dynamic-tool` parts.
    return [{ ...chunk, dynamic: true }]
  }
  if (chunk.type === "finish") {
    // Runtime drops finish.messageMetadata — lift artifacts into persistent data-artifact chunks.
    return [
      ...(finishMetadataOf(chunk)?.artifacts ?? []).map((artifact): AiSdkUIMessageChunk => ({
        type: "data-artifact",
        id: artifact.id,
        data: artifact,
        transient: false,
      })),
      chunk,
    ]
  }
  return [chunk]
}

/**
 * Wrap a transport stream so every chunk is normalized (cancellation propagates); optional sinks tap the early
 * thread id and finish metadata. `onSettled` fires exactly once — from `flush` on a clean drain, or from `cancel`
 * when the consumer tears the stream down (ChatBox `stopStreaming`) — reporting whether the turn committed (a
 * finish carrying a messageId), so a caller can roll back an early adoption whose turn ended without committing
 * (Stop / error). Firing on cancel too is essential: a Stop skips `flush`, so without it the rollback would never
 * run and the dead thread id would 409 the next send. Tracking it here, downstream of the transport, avoids the
 * race where an upstream settle (guardedStream close) runs before the finish chunk is processed.
 */
export function normalizeChunkStream(
  source: ReadableStream<AiSdkUIMessageChunk>,
  sinks: ChunkStreamSinks = {},
): ReadableStream<AiSdkUIMessageChunk> {
  const { onFinishMetadata, onEarlyThreadId, onSettled, onFailedTurnAssistantId, onStreamRetryingChange } = sinks
  let committed = false
  let settled = false
  let isCurrentlyRetrying = false
  const settle = () => {
    if (settled) return
    settled = true
    onSettled?.(committed)
  }
  // Typed via the TransformStream constructor's own param (the bare `Transformer` interface isn't an eslint global).
  // `cancel` — invoked by the runtime on a consumer cancel — is missing from this TS lib's DOM types, so it's added
  // via intersection, making the cancel-to-settle hook's intent explicit.
  type ChunkTransformer = NonNullable<ConstructorParameters<typeof TransformStream<AiSdkUIMessageChunk, AiSdkUIMessageChunk>>[0]>
  const transformer: ChunkTransformer & { cancel?: () => void } = {
    transform(chunk, controller) {
      if (onFailedTurnAssistantId) {
        const failedTurn = failedTurnAssistantIdOf(chunk)
        if (failedTurn !== null) onFailedTurnAssistantId(failedTurn.assistantMessageId, failedTurn.threadId)
      }
      if (onEarlyThreadId) {
        const earlyThreadId = earlyThreadIdOf(chunk)
        if (earlyThreadId !== null) onEarlyThreadId(earlyThreadId)
      }
      if (onStreamRetryingChange) {
        // Only report a transition — the alternative fires once per chunk, enqueuing a redundant setState for
        // every one of the (possibly hundreds of) chunks in a turn that never retried.
        const retrying = isStreamRetryingChunk(chunk)
        if (retrying !== isCurrentlyRetrying) {
          isCurrentlyRetrying = retrying
          onStreamRetryingChange(retrying)
        }
      }
      for (const normalized of normalizeChunk(chunk)) {
        const metadata = finishMetadataOf(normalized)
        if (metadata !== null) {
          // A committed reply carries a messageId; a cancelled/abort finish does not, so it never counts.
          if (metadata.messageId != null) committed = true
          onFinishMetadata?.(metadata)
        }
        controller.enqueue(normalized)
      }
    },
    flush() {
      settle()
    },
    // Consumer cancel (Stop / nav-away) skips flush — settle here so an uncommitted early adoption still rolls back.
    cancel() {
      settle()
    },
  }
  return source.pipeThrough(new TransformStream<AiSdkUIMessageChunk, AiSdkUIMessageChunk>(transformer))
}
